import React from 'react'
import {useSelector} from 'react-redux'
import styled from '@emotion/styled'
import {ContentHotel} from '@findhotel/sapi'
import {HotelOfferEntity, SearchOffer} from 'modules/sapiSearch/slice'

import {Button} from '@vio/atlas/Button'
import {Divider} from '@vio/atlas/Divider'
import {Box} from '@vio/atlas/helpers/Box'
import {Icon} from '@vio/atlas/Icon'
import {ANIMATION_TYPES, Overlay} from '@vio/atlas/Overlay'
import {cssTheme} from '@vio/atlas/src/themes'
import {Text} from '@vio/atlas/Text'
import {getIsRtlLanguage} from '@vio/core/src/auth/modules/selectors'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'

import {usePriceTrendPanelData} from '../../hooks/usePriceTrendPanelData'
import {PriceTrendsParams} from '../../services/priceInsightsApi'
import {PriceTrendContent, PriceTrendsTrackingContext} from '../../types'
import {BetterValueHotel} from '../BetterValueCarousel/types'
import {PriceTrendDetailContent} from '../PriceTrendDetailContent'

const SIDE_PANEL_WIDTH = 480
const FOOTER_HEIGHT = 76

const ScrollableContent = styled.div<{hasFooter: boolean}>`
  flex: 1;
  overflow-y: scroll;
  height: ${({hasFooter}) =>
    hasFooter ? `calc(100% - ${FOOTER_HEIGHT}px)` : '100%'};
  padding: 0 16px;
  scrollbar-width: none; /* Firefox */
  &::-webkit-scrollbar {
    display: none; /* Chrome/Safari */
  }
`

const FooterSpacer = styled.div`
  height: ${FOOTER_HEIGHT}px;
  flex-shrink: 0;
`

const FixedFooter = styled.div`
  position: absolute;
  bottom: 0;
  width: 100%;
  background: ${cssTheme.colors.background.neutral.primary};
`

interface Props {
  isOpen: boolean
  hotel: ContentHotel
  onClose: () => void
  content: PriceTrendContent | null
  offerPrice: number | null
  /**
   * Current hotel's NIGHTLY price. Forwarded to the better-value carousel
   * filter so it compares like-for-like against candidate hotels' nightly
   * rates regardless of the user's `showTotalPrices` preference.
   */
  nightlyOfferPrice: number | null
  fetchParams: Partial<PriceTrendsParams>
  offerButton: React.ReactNode
  recommendationLabel: string
  onViewed?: () => void
  onTooltipClick?: (infoCardName: string) => void
  onCarouselDisplayed?: (hotels: BetterValueHotel[]) => void
  onCarouselScrolled?: () => void
  onCarouselHotelClicked?: (
    hotel: BetterValueHotel,
    position: number,
    offer: SearchOffer,
    offerEntity: HotelOfferEntity
  ) => void
  onCarouselSeeMoreClicked?: () => void
  priceTrendsContext?: PriceTrendsTrackingContext
}

export const PriceTrendSidePanel = ({
  hotel,
  isOpen,
  onClose,
  content,
  offerPrice,
  nightlyOfferPrice,
  fetchParams,
  offerButton,
  recommendationLabel,
  onViewed,
  onTooltipClick,
  onCarouselDisplayed,
  onCarouselScrolled,
  onCarouselHotelClicked,
  onCarouselSeeMoreClicked,
  priceTrendsContext
}: Props) => {
  const isRtlLanguage = useSelector(getIsRtlLanguage)

  const {
    priceHistory,
    isPriceHistoryLoading,
    betterValueHotels,
    isBetterValueLoading,
    isHigherThanSimilar,
    shouldHideOfferButton,
    locationName,
    handleBetterValueHotelClick,
    handleSeeMoreClick
  } = usePriceTrendPanelData({
    fetchParams,
    isOpen,
    content,
    currentNightlyPrice: nightlyOfferPrice,
    hotel,
    priceTrendsContext,
    onViewed,
    onCarouselHotelClicked,
    onCarouselSeeMoreClicked
  })

  if (!content) return null
  if (!fetchParams?.checkIn || !fetchParams?.checkOut) return null

  return (
    <Overlay
      animatePresenceInitial
      enableBodyScrollLock
      transitionDuration={300}
      isOpen={isOpen}
      onShadeBackgroundClick={onClose}
      containerStyles={{
        width: SIDE_PANEL_WIDTH,
        ...(isRtlLanguage ? {left: 0, right: 'auto'} : {right: 0, left: 'auto'})
      }}
      animationType={
        isRtlLanguage ? ANIMATION_TYPES.SLIDE_LEFT : ANIMATION_TYPES.SLIDE_RIGHT
      }
      onClose={onClose}
      closeOnEsc={true}
    >
      <Box
        display="flex"
        flexDirection="row"
        alignItems="center"
        justifyContent="center"
        paddingTop="s300"
        paddingBottom="s300"
        paddingRight="s400"
        paddingLeft="s400"
        position="relative"
      >
        <Box
          display="flex"
          flexDirection="row"
          alignItems="center"
          gap="s300"
          paddingTop="s400"
          paddingBottom="s400"
        >
          <Icon name="Sparkles" colorPath="content.brand.primary" />
          <Text variant="titleM">
            <FormattedMessageWrapper
              id="priceInsights"
              defaultMessage="Price insights"
            />
          </Text>
        </Box>
        <Box
          position="absolute"
          style={isRtlLanguage ? {left: 16} : {right: 16}}
        >
          <Button onClick={onClose} variant="ghost">
            <Icon name="Close" colorPath="content.neutral.primary" />
          </Button>
        </Box>
      </Box>
      <Divider />
      <ScrollableContent hasFooter={!shouldHideOfferButton && !!offerButton}>
        <PriceTrendDetailContent
          hotel={hotel}
          checkIn={fetchParams.checkIn}
          checkOut={fetchParams.checkOut}
          content={content}
          recommendationLabel={recommendationLabel}
          offerPrice={offerPrice}
          priceHistory={priceHistory}
          isPriceHistoryLoading={isPriceHistoryLoading}
          betterValueHotels={betterValueHotels}
          isBetterValueLoading={isBetterValueLoading}
          isHigherThanSimilar={isHigherThanSimilar}
          locationName={locationName}
          priceTrendsContext={priceTrendsContext}
          onTooltipClick={onTooltipClick}
          onCarouselDisplayed={onCarouselDisplayed}
          onCarouselScrolled={onCarouselScrolled}
          onCarouselHotelClicked={handleBetterValueHotelClick}
          onCarouselSeeMoreClicked={handleSeeMoreClick}
        />
        {!shouldHideOfferButton && offerButton && <FooterSpacer />}
      </ScrollableContent>
      {!shouldHideOfferButton && offerButton && (
        <FixedFooter>
          <Divider />
          <Box
            display="block"
            paddingTop="s400"
            paddingBottom="s400"
            paddingRight="s400"
            paddingLeft="s400"
          >
            {offerButton}
          </Box>
        </FixedFooter>
      )}
    </Overlay>
  )
}
